import React, { useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import theme from '@/styles/theme';
import { EAuthModalPage } from '@/store/types';
import { actions } from '@/store';
import Message from '@/constant/message';
import Modal from '@/components/common/common-modal';
import media from '@/styles/media';
import { touchHandler } from '@/utils/touch-event-handler';
import useSelectorUser from '@/hooks/use-selector-user';
import { BasicButton, BasicButtonLabel, ButtonDiv } from '../common/new-auth-button';
import { Wrapper } from '../common/new-auth-layout';
import { Logo } from '../common/auth-layout/logo';
import MobileLogo from '../common/auth-layout/mobile-logo';

interface SelectUserProps {
}

const SelectUser:React.FC<SelectUserProps> = () => {
  const { gotoAuthLogin } = useSelectorUser('authModal');
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement|null>(null);
  const startXRef = useRef<number>(0);

  const gotoLogin = useCallback(() => {
    if (gotoAuthLogin) {
      gotoAuthLogin();
    }
    // dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.INPUT_EMAIL));
  }, [gotoAuthLogin]);

  const gotoGeneralSignup = useCallback(() => {
    dispatch(actions.user.authModal.setRegister('general'));
    dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.ARGREE_POLICY));
  }, [dispatch]);

  const gotoCreatorSignup = useCallback(() => {
    dispatch(actions.user.authModal.setRegister('creator'));
    dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.ARGREE_POLICY));
  }, [dispatch]);

  const onDragEnd = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.style.pointerEvents = 'auto';
      containerRef.current.style.cursor = 'default';
    }
    document.removeEventListener('mousemove', onDragMove);
    document.removeEventListener('mouseup', onDragEnd);
    document.removeEventListener('touchmove', touchHandler);
    document.removeEventListener('touchend', touchHandler);
  }, []);

  const onDragStart = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (scrollRef.current !== null) {
      const start = e.pageX + scrollRef.current.scrollLeft;
      startXRef.current = start;
      document.addEventListener('mousemove', onDragMove);
      document.addEventListener('mouseup', onDragEnd);
      document.addEventListener('touchmove', touchHandler);
      document.addEventListener('touchend', touchHandler);
    }
  }, [onDragEnd]);

  const onDragMove = (e: MouseEvent) => {
    if (scrollRef.current !== null) {
      const { scrollWidth, clientWidth, scrollLeft } = scrollRef.current;
      scrollRef.current.scrollLeft = startXRef.current - e.pageX;

      if (scrollLeft === 0) {
        startXRef.current = e.pageX;
      } else if (scrollWidth <= clientWidth + scrollLeft) {
        startXRef.current = e.pageX + scrollLeft;
      }
    }
  };
  return (
    <Modal show closeOnClickOverlay={false} backgroundOpacity={1}>
      <Container>
        <Header>
          <Logo />
          <HeaderButtonDiv className="signUpPage-login">
            <BasicButtonLabel>
              {`${t(Message.이미계정이있으신가요)}`}
            </BasicButtonLabel>
            <BasicButton
              onClick={gotoLogin}
            >
              {t(Message.로그인)}
            </BasicButton>
          </HeaderButtonDiv>
          <MobileLogo />
        </Header>
        <Contents>
          <Title>{t(Message.회원가입)}</Title>
          <Text>
            {t(Message.스팀업에오신걸환영합니다)}
            <br />
            {t(Message.회원이되시면보다다양한혜택을받아보실수있습니다)}
          </Text>
          <SemiBoldText>{t(Message.회원유형을선택해주세요)}</SemiBoldText>
          <CardsWrap>
            <Cards
              ref={scrollRef}
              onMouseDown={onDragStart}
              onTouchStart={touchHandler}
              className="cards"
            >
              <Card onClick={gotoGeneralSignup}>
                <CardBackground>
                  <CardImg src="/assets/images/auth/general-signup.png" alt="일반 회원" />
                </CardBackground>
                <CardContents>
                  <TextContainer>
                    <CardTitle>{t(Message.일반회원)}</CardTitle>
                    <CardText>{t(Message.스팀업사이트에서제공하는서비스를이용하는회원)}</CardText>
                  </TextContainer>
                  <ButtonContainer>
                    <CardButton>{t(Message.선택하기)}</CardButton>
                  </ButtonContainer>
                </CardContents>
              </Card>

              <Card onClick={gotoCreatorSignup} className="teacher">

                <CardBackground>
                  <CardImg src="/assets/images/auth/instructor-signup.png" alt="강사 회원" />
                </CardBackground>

                <CardContents>
                  <TextContainer>
                    <CardTitle>{t(Message.강사회원)}</CardTitle>
                    <CardText className="teacher">{t(Message.클래스등록및운영관리서비스를이용하는회원)}</CardText>
                  </TextContainer>
                  <ButtonContainer>
                    <CardButton>{t(Message.선택하기)}</CardButton>
                  </ButtonContainer>
                </CardContents>
              </Card>
            </Cards>
          </CardsWrap>
        </Contents>
      </Container>
    </Modal>
  );
};

export default SelectUser;

const TextContainer = styled.div`
  display:flex;
  flex-direction: column;
  gap: 12px;
`;

const ButtonContainer = styled.div`
  padding-bottom: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Container = styled(Wrapper)`
  flex-direction: column;  
  overflow: hidden auto;
  justify-content: unset;
`;

export const Header = styled.div`
  display: flex;
  width: 100%;
  padding: 0 0 0 53px;
  align-items: center;
  justify-content: space-between;
  ${media.lg`
    padding: 30px 0 40px 0;
    justify-content: center;
  `}
`;

export const HeaderButtonDiv = styled(ButtonDiv)`
  padding: 20px 50px;
  height: unset;
  &.signUpPage-login{
    ${media.lg`
      display: none;
    `}
  }
`;

const Contents = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const Title = styled.div`  
  font-family:  ${theme.fontFamily.bold};
  font-size: 40px;
  line-height: 48px;
  text-align: center;
  ${media.lg`
    font-size: 30px;
  `}
  ${media.xs`
    font-size: 26px;
  `}
`;
const Text = styled.div`
  padding: 25px 0px 36px;
  font-family: ${theme.fontFamily.regular};
  font-size: 16px;
  line-height: 26px;
  text-align: center;
  ${media.lg`
    font-family: ${theme.fontFamily.medium};
    padding: 25px 0px;
  `}
  ${media.xs`
    font-size: 14px;
    line-height: 22px;   
    padding: 12px 0px 36px;
  `}
`;
const SemiBoldText = styled.div`
  font-family:${theme.fontFamily.semiBold};
  font-size: 22px;
  line-height: 26px;
  text-align: center;
  padding-bottom: 10px;
  ${media.xs`
    font-size: 16px;
    padding-bottom: 20px;
  `}
`;

const CardsWrap = styled.div`
  /* display: none; */
  ${media.lg`  
    width: 100%;
    display: flex;
    justify-content: center;
  `}
`;
const Cards = styled.div`
  display: flex;
  margin: 0 auto;
  ${media.lg`
    overflow-x: scroll;
    cursor: pointer;
    ::-webkit-scrollbar {
      display: none;
    }
  `}
`;

const CardBackground = styled.div`
  width: 324px;
  height: 215px;
  border-radius: 38px;
  background: radial-gradient(50% 50% at 50% 50%, #5B708D 0%, #3A4758 100%);
  ${media.xs`
    width: 280px;
    height: 160px;
  `}
`;

const CardImg = styled.img`
  width: 158px;
  height: 189px;
  padding: 26px 83px 0;
  ${media.xs`
    width: 120px;
    height: 143px;
    padding: 17px 83px 0;
  `}
`;

const CardContents = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 32px 32px 0 32px;
  ${media.xs`
    padding: 6px 0;
  `}
`;

const CardTitle = styled.div`
  font-family: ${theme.fontFamily.semiBold};
  font-size: 26px;
  line-height: 31px;
  text-align: center;
  ${media.xs`
    font-family: ${theme.fontFamily.bold};
    font-size: 22px;
  `}
`;

const CardText = styled.div`
  /* padding: 15px 0 36px 0; */
  font-family: ${theme.fontFamily.regular};
  font-size: 15px;
  line-height: 22px;
  text-align: center;
  ${media.lg`
    /* padding: 21px 0 30px 0; */
    &.teacher {
      /* padding: 21px 8px 30px 8px; */
    }
  `}
  ${media.xs`
    font-family: ${theme.fontFamily.medium};
    font-size: 14px;
    padding: 15px 61px 0px 61px;

    /* padding: 15px 61px 36px 61px; */
    &.teacher {
      padding: 15px 69px 36px 69px;
    }
  `}
`;

const CardButton = styled.button`
  background-color: #4B586A;
  padding: 16px 48px;
  border-radius: 8px;
  border: none;

  font-family: ${theme.fontFamily.regular};
  font-size: 15px;
  line-height: 19px;
  text-align: center;
  color: #FFFFFF;
  cursor: pointer;
  ${media.xs`
    font-family: ${theme.fontFamily.semiBold};
    font-size: 16px;
  `}
`;

const Card = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 360px;
  margin: 40px;
  padding: 16px 18px;
  border-radius: 50px;
  background: #FFFFFF;
  box-shadow: 10px 10px 30px rgba(84, 100, 224, 0.1);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 480px;

  &:hover {
    ${CardBackground} {
      background: radial-gradient(50% 50% at 50% 50%, #9076F9 0%, #5F3FDF 100%);
    }
    ${CardButton} {
      background: ${theme.color.purple};
    }
  }
  ${media.lg`
    margin: 20px 0 20px 20px;
    &.teacher{
      margin-right: 20px;
    }
  `}
`;
